// Shared marketplace constants (must match upstream enums)
// Age split (Feb 2026 refresh): 0-5, 6-10, 11-15, 16+. Mapped to upstream category enums.
//
// Iteration 173 — the combined "Free / Giveaways / Exchange" category
// has been split into two independent chips so the Sell-a-Toy form
// mirrors the Browse filter row exactly (Free giveaways / Exchange /
// Other). Both new slugs (`free`, `exchange`) exist in the backend
// CATEGORY_MAP already, so listings tagged either way surface under
// their own filter chip.
export const CATEGORIES = [
  { value: "baby_toddler", label: "Baby-Toddler",     ageHint: "0–5 yrs" },
  { value: "junior_hub",   label: "Tiny Traders",     ageHint: "6–10 yrs" },
  { value: "collectors",   label: "Tween Collectors", ageHint: "11–15 yrs" },
  { value: "teen",         label: "Teens",            ageHint: "16+ yrs" },
  { value: "free",         label: "Free giveaways",   ageHint: "" },
  { value: "exchange",     label: "Exchange / Swap",  ageHint: "" },
  // Iteration 274 — Clothing is a web-tier category (attributes live in
  // listing_metadata; upstream stores these listings under "other").
  { value: "clothing",     label: "Clothing",         ageHint: "" },
  { value: "other",        label: "Other",            ageHint: "" },
];

// Values that behave as mutually-exclusive "listing type" chips (they
// clear all age picks and can't be combined with each other). Kept
// separate from age slugs so the KidWorkspace picker stays generic.
// Iteration 274 — "clothing" joins the exclusive set (its size/gender
// attributes replace the age-band chips).
export const LISTING_TYPE_SLUGS = new Set(["free", "exchange", "clothing", "other"]);

// Legacy upstream slug "exchange" still exists in some records — the picker no
// longer offers it but the mapper accepts it as a synonym for "free" so old
// listings still surface correctly under the new combined chip.
export const LEGACY_EXCHANGE_ALIAS = "exchange";

// Curated safe tag chips for kid-side listing creation. No free text means
// the AI moderation surface stays predictable and the parent doesn't need
// to review novel words.
export const SAFE_TAG_CHIPS = [
  "LEGO", "Pokemon", "Books", "Board Games",
  "Cards", "Plushies", "Puzzles", "Crafts",
];

// Pre-selected price steps for the kid form. Kids cannot type a custom
// price — they tap one of these buttons (or pick "Free" via the PWYC
// toggle) so pricing stays age-appropriate.
export const KID_PRICE_STEPS_PENCE = [
  50, 100, 150, 200, 300, 500, 800, 1000, 1500, 2000,
];

// Curated title chips — kids pick from these instead of typing so the
// listing pipeline never has to moderate free-form kid text.
export const KID_TITLE_CHIPS = [
  "Board Game", "Book", "LEGO Set", "Toy Car",
  "Cards Set", "Plush Toy", "Puzzle", "Doll",
  "Art & Craft Set", "Video Game", "Sports Toy", "Figurine",
  "Play Kitchen", "Musical Toy", "Dress-Up", "Clothing", "Other Toy",
];

export const CONDITIONS = [
  {
    value: "brand_new",
    label: "Brand New",
    tooltip: "Unopened in original box/packaging. Never used.",
  },
  {
    value: "almost_new",
    label: "Almost New",
    tooltip: "Opened or set up once or twice, but practically untouched with zero signs of wear.",
  },
  {
    value: "used_good",
    label: "Used - Good Condition",
    tooltip: "Gently played with and fully functional. May show minor, light signs of normal use.",
  },
  {
    value: "well_loved",
    label: "Well Loved",
    tooltip: "Works great and has lots of play left, but shows visible wear, marks, or scuffs.",
  },
];

const CONDITION_ALIASES = {
  new: "brand_new",
  brand_new: "brand_new",
  excellent: "brand_new",
  "brand new": "brand_new",
  like_new: "almost_new",
  "like-new": "almost_new",
  almost_new: "almost_new",
  "almost new": "almost_new",
  good: "used_good",
  played_with: "used_good",
  "played with": "used_good",
  used_good: "used_good",
  "used - good condition": "used_good",
  fair: "well_loved",
  well_loved: "well_loved",
  "well loved": "well_loved",
};

export const normalizeCondition = (v) => CONDITION_ALIASES[String(v || "").trim().toLowerCase()] || v || "";

export const AGE_GROUPS = [
  { value: "",              label: "All ages" },
  { value: "baby_toddler",  label: "Baby-Toddler · 0–5 yrs" },
  { value: "junior_hub",    label: "Tiny Traders · 6–10 yrs" },
  { value: "collectors",    label: "Tween Collectors · 11–15 yrs" },
  { value: "teen",          label: "Teens · 16+ yrs" },
];

// Iteration 75 — parcel sizes match the mobile spec (Feb 2026 refresh).
// UPSTREAM CONSTRAINT: the ToyCreate schema only accepts {small, medium}.
// The backend proxy coerces `large` and `bulky` to `medium` for the
// upstream call and preserves the ACTUAL selected value in
// `local_db.listing_metadata` (keyed by listing_id). Read paths merge
// the true value back so listing detail / seller dashboard / approval
// cards all see the size the child actually picked.
export const PACKAGE_SIZES = [
  { value: "small",   label: "Small (Royal Mail)" },
  { value: "medium",  label: "Medium (Royal Mail)" },
  { value: "large",   label: "Large (Royal Mail)" },
  { value: "bulky",   label: "Bulky Item" },
];

// Parcel sizes that ship via Royal Mail Post (i.e. NOT bulky). These
// auto-default their fulfillment to `home_delivery` on the child form.
export const ROYAL_MAIL_SIZES = ["small", "medium", "large"];

export const formatGBP = (amount) => {
  const n = Number(amount || 0);
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);
};

export const conditionLabel = (v) => CONDITIONS.find(c => c.value === normalizeCondition(v))?.label || v || "—";
export const categoryLabel  = (v) => CATEGORIES.find(c => c.value === v)?.label || v || "—";

// Fee breakdown — MATCHES MOBILE APP SPEC (Feb 2026 refresh)
// Family Safety & Buyer Protection Fee: 10% flat of item price.
//   • 5% Family Safety Verification (ID checks, AI moderation, parent PIN)
//   • 5% Buyer Order Protection & Hosting (escrow, dispute cover, infra)
// Sellers retain 100% of the item price (seller fee is 0%).
// Postage is paid SEPARATELY by the seller directly at the Post Office —
// the platform never charges a handling / delivery fee.
// For EXTERNAL transactions (parent Stripe checkout) the Stripe processing
// fee (~1.5% + 20p UK card) is displayed as a separate line and passed
// through to the buyer. INTERNAL transactions (Kid Piggy Bank) show only
// the 10% safety fee.
export const SELLER_FEE_PERCENT = 0;
export const PLATFORM_FEE_PERCENT = 0.10;
// Iteration 172 — Royal Mail Post now carries a flat £3.50 postage line
// that is charged to the buyer at checkout and passed straight through to
// the seller so they can print the label. Parent Handoff and School
// Collection remain £0.00.
export const HOME_DELIVERY_HANDLING_GBP = 3.50;
// Stripe UK card fee — used only for the external-checkout display line.
export const STRIPE_PROCESSING_PERCENT = 0.015;
export const STRIPE_PROCESSING_FIXED_GBP = 0.20;

export const FEE_TOOLTIP_COPY =
  "Covers Family Safety Verification (5%) and Buyer Order Protection & Hosting (5%).";

// Returns a fee breakdown. `external=true` (parent Stripe checkout) adds
// the Stripe processing line. `external=false` (Kid Piggy Bank) shows the
// 10% safety fee only. `postage_gbp` is added straight to the buyer total
// (Royal Mail Post = £3.50) and NOT subject to Stripe or safety fees.
export const computeFees = (gross, { external = true, postage_gbp = 0 } = {}) => {
  const g = Number(gross || 0);
  const post = Number(postage_gbp || 0);
  if (g <= 0) {
    return {
      gross: 0,
      safety_fee: 0,
      stripe_fee: 0,
      postage: post,
      total: 0,
      net_to_seller: 0,
      buyer_total: post,
      external,
    };
  }
  const safety_fee = +(g * PLATFORM_FEE_PERCENT).toFixed(2);
  const pre_stripe = +(g + safety_fee).toFixed(2);
  const stripe_fee = external
    ? +(pre_stripe * STRIPE_PROCESSING_PERCENT + STRIPE_PROCESSING_FIXED_GBP).toFixed(2)
    : 0;
  const buyer_total = +(pre_stripe + stripe_fee + post).toFixed(2);
  return {
    gross: g,
    safety_fee,
    stripe_fee,
    postage: post,
    total: safety_fee, // legacy alias — remains the "platform fee" amount
    net_to_seller: g,  // sellers keep 100% of item price
    buyer_total,
    external,
  };
};

// Fulfillment options — MATCHES MOBILE APP SPEC + Iteration 208 refresh
// Postage is ALWAYS handled off-platform by the seller. The `handling`
// field is kept at 0 for backwards-compatibility with any callers that
// still reference it, but no fulfillment adds a fee to the buyer.
//
// Iteration 208 — canonical set of 4 selectable fulfillment modes:
//   • parent_handoff  "Parent Handoff"          (local, kid-to-kid via parents)
//   • school_gate     "At/After School Collection"
//   • local_shop      "Local Shop Pick-Up"      (NOW ENABLED)
//   • home_delivery   "Royal Mail Post"
//
// `collection_only` and `delivery_only` are NO LONGER selectable
// fulfillment modes — they've become seller-perspective VISUAL BADGES
// under Parent Handoff, chosen in the Edit modal and rendered via
// ListingBadges.jsx (read-only on public cards).
export const FULFILLMENT_OPTIONS = [
  {
    value: "parent_handoff",
    label: "Parent Handoff",
    subtitle: "Meet up parent-to-parent",
    handling: 0.00,
    disabled: false,
    tone: "pink",
    bulkyOnly: false,
  },
  {
    value: "school_gate",
    label: "At/After School Collection",
    subtitle: "Handover at the school gate",
    handling: 0.00,
    disabled: false,
    tone: "teal",
    bulkyOnly: false,
  },
  {
    value: "local_shop",
    label: "Local Shop Pick-Up",
    subtitle: "Partner shops near you",
    handling: 0.00,
    // Iteration 263 — permanently unavailable platform-wide ("Coming
    // Soon" badge on the tile). Only Parent Handoff + At/After School
    // Collection remain selectable for local collection.
    disabled: true,
    comingSoon: true,
    tone: "slate",
    bulkyOnly: false,
  },
  {
    value: "home_delivery",
    label: "Royal Mail Post",
    subtitle: "Posted by the seller",
    handling: 3.50,
    disabled: false,
    tone: "slate",
    bulkyOnly: false,
  },
];

// Iteration 208 — Parent Handoff visual sub-badges (NOT selectable
// fulfillment modes). Sellers pick these in the Edit modal and they
// render as read-only tags on public listing cards.
export const PARENT_HANDOFF_BADGES = [
  {
    value: "collection_only",
    label: "Collection Only",
    subtitle: "Buyer collects from seller",
    tone: "amber",
  },
  {
    value: "delivery_only",
    label: "Delivery Only",
    subtitle: "Seller drops off at buyer",
    tone: "amber",
  },
];

export const fulfillmentOption = (value) =>
  FULFILLMENT_OPTIONS.find((f) => f.value === value) || FULFILLMENT_OPTIONS[0];

// Courier hints — Iteration 153: Royal Mail is the default carrier for
// all UK household shipments unless upstream / seller explicitly
// stamps Evri (large parcel size). Removed the ambiguous
// "Royal Mail / Evri" combined string.
export const courierFromPackageSize = (size) => {
  if (size === "large") return { name: "Evri", emoji: "📦" };
  return { name: "Royal Mail", emoji: "📮" };
};

// ---------------------------------------------------------------------------
// Iteration 274 — Clothing category (web-only; upstream stores clothing
// listings under "other" and the attributes live in listing_metadata).
// Shared by the parent create form, the Kid Zone tap-chip form, the
// browse filter row and listing display surfaces.
// ---------------------------------------------------------------------------
export const CLOTHING_SUBCATEGORIES = [
  { value: "tops_tshirts", label: "Tops & T-Shirts" },
  { value: "jumpers_hoodies", label: "Jumpers & Hoodies" },
  { value: "trousers_jeans", label: "Trousers & Jeans" },
  { value: "dresses_skirts", label: "Dresses & Skirts" },
  { value: "coats_jackets", label: "Coats & Jackets" },
  { value: "school_uniform", label: "School Uniform" },
  { value: "fancy_dress", label: "Fancy Dress & Costumes" },
  { value: "sleepwear", label: "Sleepwear & Nightwear" },
  { value: "shoes_footwear", label: "Shoes & Footwear" },
  { value: "bundles", label: "Bundles / Multi-packs" },
];

export const CLOTHING_GENDERS = [
  { value: "boys", label: "Boys" },
  { value: "girls", label: "Girls" },
  { value: "unisex", label: "Unisex" },
  { value: "baby", label: "Baby (0–24M)" },
];

export const CLOTHING_SIZE_BANDS = [
  "Newborn", "0-3M", "3-6M", "6-9M", "9-12M", "12-18M", "18-24M",
  "2-3Y", "3-4Y", "4-5Y", "5-6Y", "6-7Y", "7-8Y", "8-9Y", "9-10Y",
  "10-11Y", "11-12Y", "12-13Y", "14Y+",
];

export const CLOTHING_FOOTWEAR_SIZES = [
  ...[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => `Infant ${i}`),
  ...[10, 11, 12, 13, 1, 2, 3].map((i) => `Child ${i}`),
  ...[4, 5, 6].map((i) => `Junior ${i}`),
];

export const CLOTHING_BRANDS = [
  "Next", "M&S", "Boden", "Zara", "H&M", "Tu", "George", "Nutmeg",
  "Nike", "Adidas", "Other",
];

export const clothingSubcategoryLabel = (value) =>
  (CLOTHING_SUBCATEGORIES.find((s) => s.value === value) || {}).label || value || "";

export const clothingGenderLabel = (value) =>
  (CLOTHING_GENDERS.find((g) => g.value === value) || {}).label || value || "";

// True when a listing belongs to the web-only clothing category.
export const isClothingListing = (listing) =>
  !!listing && (listing.category_web === "clothing" || listing.category === "clothing");

// One-line attribute summary for cards, detail pages and ledger rows:
// "3-4Y · Girls · Next"
export const clothingSummary = (listing) => {
  if (!isClothingListing(listing)) return "";
  return [
    listing.clothing_size,
    clothingGenderLabel(listing.clothing_gender),
    listing.clothing_brand,
  ].filter(Boolean).join(" · ");
};
